import { faComputerMouse } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { addToast, nextId } from "shared/actions/toastActions";

import Spinner from "components/Spinner";
import { useAppDispatch } from "store/hooks";

import { useSpaceMouseContext } from "./SpaceMouseProvider";
import { SpaceMouseService } from "./SpaceMouseService";

interface SpaceMouseConnectButtonProps {
  className?: string;
}

export function SpaceMouseConnectButton({
  className,
}: SpaceMouseConnectButtonProps) {
  const dispatch = useAppDispatch();
  const { status, isSupported, connect, disconnect, deviceInfo } =
    useSpaceMouseContext();

  if (!isSupported) {
    return null;
  }

  const handleClick = async () => {
    const success = await connect();
    if (!success) {
      dispatch(
        addToast(
          {
            kind: "danger",
            title: "Failed to connect to Space Mouse",
            message:
              "If the 3DConnexion driver is running, quit it first:\\n" +
              "• macOS: Quit from menu bar or run: killall 3DconnexionHelper\\n" +
              "• Windows: Exit from system tray\\n\\n" +
              "Then unplug and replug the device and try again.",
          },
          nextId()
        )
      );
    }
  };

  let icon = <FontAwesomeIcon icon={faComputerMouse} />;
  let buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement> = {
    className,
    title: "Click to authorize 3DConnexion Space Mouse (first time only)",
    onClick: handleClick,
    style: undefined,
    disabled: false,
  };

  if (status === "connecting") {
    icon = (
      <Spinner size="16px" width="3px" frontColor="black" backColor="#d8d8d8" />
    );
    buttonProps.disabled = true;
    buttonProps.title = "Connecting...";
  } else if (status === "connected") {
    buttonProps.onClick = disconnect;
    buttonProps.title = `Connected to ${
      deviceInfo?.name || "Space Mouse"
    }. Click to disconnect.`;
    buttonProps.style = {
      backgroundColor: "var(--teal)",
      color: "white",
    };
  } else if (status === "error") {
    buttonProps.style = { backgroundColor: "var(--red)", color: "white" };
    buttonProps.title = "3D Mouse Error - Retry";
  }

  return <button {...buttonProps}>{icon}</button>;
}

export function SpaceMouseConnectButtonStandalone({
  className,
}: SpaceMouseConnectButtonProps) {
  if (!SpaceMouseService.isSupported()) {
    return null;
  }
  return <SpaceMouseConnectButton className={className} />;
}
